import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Target, Flame, Star, Users, Crown, TrendingUp, Settings, Lock, Globe, Share2, Copy, Check, Link2 } from "lucide-react";

const Performer = ({ icon: Icon, label, name, value, testid }) => (
  <div className="surface-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 label-tag mb-2"><Icon size={12}/>{label}</div>
    <div className="font-display text-xl">{name || "—"}</div>
    {value && <div className="text-xs uppercase tracking-widest text-primary-accent mt-1">{value}</div>}
  </div>
);

export default function GroupProfile() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/groups/${groupId}`);
      setProfile(data);
      const isAdmin = user?.role === "admin" || (user?.group_admin_ids || []).includes(groupId);
      if (isAdmin && data?.tournament_id) {
        try {
          const pr = await api.get(`/tournaments/${data.tournament_id}/groups/pending-requests-count`);
          setPendingCount(pr.data?.by_group?.[groupId] || 0);
        } catch (err) { console.warn("pending count failed:", err?.message || err); }
      }
    })();
  }, [groupId, user]);

  if (!profile) return null;
  const p = profile.top_performers || {};
  const canAdmin = user?.role === "admin" || (user?.group_admin_ids || []).includes(groupId);
  const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const inviteSlug = [slugify(profile.tournament_name), slugify(profile.name)].filter(Boolean).join("-");
  const inviteUrl = profile.join_code
    ? `${window.location.origin}/join/${profile.join_code}${inviteSlug ? `/${inviteSlug}` : ""}`
    : `${window.location.origin}/groups/${groupId}`;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied — share it with friends");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy the link"); }
  };

  const shareInvite = async () => {
    try {
      await navigator.share({
        title: `Join ${profile.name} on Jinxing Sports`,
        text: `Join my group "${profile.name}" on Jinxing Sports and predict the tournament with us!`,
        url: inviteUrl,
      });
    } catch { /* user dismissed the share sheet */ }
  };

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <Link to="/leaderboard" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="back-to-groups">
          <ArrowLeft size={14}/> Leaderboard
        </Link>
        <div className="flex items-center gap-2">
          <button
            data-testid="invite-members-btn"
            onClick={() => setShowInvite((v) => !v)}
            className="inline-flex items-center gap-2 surface-card px-4 py-2 text-xs uppercase tracking-widest text-primary-accent hover:border-primary-accent"
          >
            <Share2 size={14}/> Invite
          </button>
          {canAdmin && (
            <Link to={`/groups/${groupId}/manage`} data-testid="manage-group-btn"
              className="relative inline-flex items-center gap-2 surface-card px-4 py-2 text-xs uppercase tracking-widest text-primary-accent hover:border-primary-accent">
              <Settings size={14}/> Manage Group
              {pendingCount > 0 && (
                <span data-testid="manage-btn-pending-badge" className="min-w-[18px] h-[18px] px-1 bg-primary-accent text-white text-[10px] font-display flex items-center justify-center rounded-full" title={`${pendingCount} pending join request${pendingCount === 1 ? "" : "s"}`}>
                  {pendingCount > 9 ? "9+" : pendingCount}
                </span>
              )}
            </Link>
          )}
        </div>
      </div>

      {showInvite && (
        <div className="surface-card p-4 md:p-5 mb-4 border border-primary-accent/40" data-testid="invite-panel">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={16} className="text-primary-accent" />
            <span className="label-tag text-primary-accent">Invite members to {profile.name}</span>
          </div>
          <p className="text-xs text-zinc-400 mb-3">
            Share this link — anyone who opens it can join {profile.visibility === "invite_only" ? "this invite-only group" : "the group"}.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input readOnly value={inviteUrl} onFocus={(e) => e.target.select()}
              className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-200 focus:border-primary-accent focus:outline-none" data-testid="invite-link-value" />
            <button data-testid="copy-invite-btn" onClick={copyInvite}
              className="inline-flex items-center justify-center gap-2 bg-primary-accent text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-[var(--primary-hover)] flex-shrink-0">
              {copied ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy</>}
            </button>
            {canShare && (
              <button data-testid="share-invite-btn" onClick={shareInvite}
                className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-200 px-4 py-2 text-xs uppercase tracking-widest hover:border-primary-accent flex-shrink-0">
                <Share2 size={14}/> Share
              </button>
            )}
          </div>
        </div>
      )}

      <div className="surface-card p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: profile.color }}/>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="label-tag mb-1" style={{ color: profile.color }}>Group Profile</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">{profile.name}</h1>
            <p className="text-zinc-400 mt-2">{profile.description}</p>
            <span className={`inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${(profile.visibility === "invite_only") ? "border-zinc-700 text-zinc-400" : "border-green-500/40 text-green-400"}`} data-testid="group-visibility-badge">
              {profile.visibility === "invite_only" ? <><Lock size={10}/> Invite-only</> : <><Globe size={10}/> Public</>}
            </span>
          </div>
          <div className="flex gap-3">
            <div className="surface-card p-4 min-w-[120px] text-center">
              <div className="label-tag">Rank</div>
              <div className="font-display text-4xl text-primary-accent">{profile.rank || "—"}</div>
            </div>
            <div className="surface-card p-4 min-w-[120px] text-center">
              <div className="label-tag">Score</div>
              <div className="font-display text-4xl">{profile.score}</div>
            </div>
            <div className="surface-card p-4 min-w-[120px] text-center">
              <div className="label-tag">Members</div>
              <div className="font-display text-4xl">{profile.member_count}</div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="font-display text-2xl uppercase mb-3">Top Performers</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Performer testid="perf-highest" icon={Crown} label="Highest Ranked" name={p.highest_ranked?.name} value={p.highest_ranked ? `#${p.highest_ranked.rank} · ${p.highest_ranked.points} pts` : null}/>
        <Performer testid="perf-exact" icon={Target} label="Most Exact Scores" name={p.most_exact_scores?.name} value={p.most_exact_scores ? `${p.most_exact_scores.exact_scores} exact` : null}/>
        <Performer testid="perf-streak" icon={Flame} label="Longest Streak" name={p.longest_streak?.name} value={p.longest_streak ? `${p.longest_streak.best_streak} streak` : null}/>
        <Performer testid="perf-fantasy" icon={Star} label="Fantasy Leader" name={p.fantasy_leader?.name} value={p.fantasy_leader ? `${p.fantasy_leader.points} pts` : null}/>
      </div>

      {/* Biggest contributors for the current gameweek */}
      <h2 className="font-display text-2xl uppercase mb-3 flex items-center gap-2">
        <TrendingUp size={20} className="text-primary-accent"/> Biggest Contributors
        {profile.gameweek_contributors?.label && (
          <span className="label-tag text-zinc-500">{profile.gameweek_contributors.label}</span>
        )}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="gameweek-contributors">
        {(profile.gameweek_contributors?.contributors || []).length === 0 && (
          <div className="surface-card p-4 text-sm text-zinc-500 sm:col-span-3">No completed games yet this gameweek.</div>
        )}
        {(profile.gameweek_contributors?.contributors || []).map((c, i) => (
          <div key={c.user_id} className="surface-card p-4 flex items-center justify-between" data-testid={`contributor-${c.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className={`font-display text-3xl w-8 ${i === 0 ? "text-yellow-400" : i === 1 ? "text-zinc-300" : "text-amber-700"}`}>{i + 1}</span>
              <span className="font-display uppercase tracking-wide truncate">{c.name}</span>
            </div>
            <span className="font-display text-2xl text-primary-accent tabular-nums">+{c.points}</span>
          </div>
        ))}
      </div>

      <h2 className="font-display text-2xl uppercase mb-3 flex items-center gap-2">
        <Users size={20} className="text-primary-accent"/> Members
      </h2>
      <div className="surface-card">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
              <th className="text-left px-4 py-3 label-tag">Name</th>
              <th className="text-right px-4 py-3 label-tag">Exact</th>
              <th className="text-right px-4 py-3 label-tag">Streak</th>
              <th className="text-right px-4 py-3 label-tag">Points</th>
            </tr>
          </thead>
          <tbody>
            {(profile.members || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-8">No members in this group yet.</td></tr>
            )}
            {(profile.members || []).map((m, i) => (
              <tr key={m.user_id} className="border-b border-white/5" data-testid={`member-${m.user_id}`}>
                <td className="px-4 py-3 font-display text-xl text-zinc-400">#{m.rank}</td>
                <td className="px-4 py-3 font-display text-base tracking-wider">{m.name}</td>
                <td className="px-4 py-3 text-right text-zinc-400">{m.exact_scores}</td>
                <td className="px-4 py-3 text-right text-zinc-400">{m.best_streak}</td>
                <td className="px-4 py-3 text-right font-display text-xl text-primary-accent">{m.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
